import React, { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';
import { useNotification } from './Notification';
import { GoldenAmount, OrderStatusBadge } from './store/StoreUi';

const EMPTY_PRODUCT = {
  name: '',
  category: '',
  brand: '',
  sku: '',
  shortDescription: '',
  description: '',
  priceGolden: '',
  compareAtGolden: '',
  stock: 0,
  trackInventory: true,
  status: 'draft',
  featured: false,
  estimatedDeliveryDays: 7,
  tags: '',
  images: [],
  variants: [],
};

const ORDER_STATUS_OPTIONS = ['placed', 'processing', 'packed', 'shipped', 'delivered', 'cancelled'];

function cancellationLocked(order) {
  return (
    ['shipped', 'delivered'].includes(order?.status) ||
    (order?.timeline || []).some((entry) => ['shipped', 'delivered'].includes(entry.status))
  );
}

function AdminModal({ title, children, onClose, wide = false }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4">
      <div className={`max-h-[95vh] w-full ${wide ? 'max-w-5xl' : 'max-w-2xl'} overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 shadow-2xl`}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-5 py-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ProductEditor({ product, onClose, onSaved }) {
  const { showNotification } = useNotification();
  const [form, setForm] = useState(() => {
    if (!product) return EMPTY_PRODUCT;
    return {
      ...EMPTY_PRODUCT,
      ...product,
      tags: (product.tags || []).join(', '),
      variants: (product.variants || []).map((variant) => ({
        _id: variant._id,
        name: variant.name || '',
        sku: variant.sku || '',
        stock: variant.stock || 0,
        active: variant.active !== false,
      })),
    };
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const uploadProductImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append('image', file);
      const { data } = await api.post('/admin/store/upload-image', body);
      setField('images', [...form.images, { url: data.url, publicId: data.publicId, alt: form.name }]);
      showNotification('Product image uploaded', 'success');
    } catch (error) {
      showNotification(error?.response?.data?.message || 'Image upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setField('images', form.images.filter((_, itemIndex) => itemIndex !== index));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        priceGolden: Number(form.priceGolden),
        compareAtGolden: form.compareAtGolden ? Number(form.compareAtGolden) : null,
        stock: Number(form.stock),
        estimatedDeliveryDays: Number(form.estimatedDeliveryDays),
        tags: String(form.tags || '').split(',').map((tag) => tag.trim()).filter(Boolean),
        variants: form.variants.map((variant) => ({ ...variant, stock: Number(variant.stock) })),
      };
      if (product?._id) await api.patch(`/admin/store/products/${product._id}`, payload);
      else await api.post('/admin/store/products', payload);
      showNotification(product ? 'Product updated' : 'Product created', 'success');
      onSaved();
    } catch (error) {
      showNotification(error?.response?.data?.message || 'Could not save product', 'error');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm text-gray-900 dark:text-white';
  return (
    <AdminModal title={product ? `Edit ${product.name}` : 'Add store product'} onClose={onClose} wide>
      <form onSubmit={save} className="space-y-6 p-5 sm:p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <label><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Product name *</span><input required value={form.name} onChange={(e) => setField('name', e.target.value)} className={inputClass} /></label>
          <label><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Category *</span><input required value={form.category} onChange={(e) => setField('category', e.target.value)} className={inputClass} placeholder="Jerseys, Sneakers, Collectibles" /></label>
          <label><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Brand</span><input value={form.brand} onChange={(e) => setField('brand', e.target.value)} className={inputClass} /></label>
          <label><span className="mb-1 block text-sm font-semibold dark:text-gray-200">SKU</span><input value={form.sku} onChange={(e) => setField('sku', e.target.value)} className={inputClass} /></label>
          <label><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Golden price *</span><input required min="1" type="number" value={form.priceGolden} onChange={(e) => setField('priceGolden', e.target.value)} className={inputClass} /></label>
          <label><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Compare-at price</span><input min="1" type="number" value={form.compareAtGolden || ''} onChange={(e) => setField('compareAtGolden', e.target.value)} className={inputClass} /></label>
          <label><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Status</span><select value={form.status} onChange={(e) => setField('status', e.target.value)} className={inputClass}><option value="draft">Draft</option><option value="active">Active</option><option value="archived">Archived</option></select></label>
          <label><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Est. delivery days</span><input type="number" min="1" max="120" value={form.estimatedDeliveryDays} onChange={(e) => setField('estimatedDeliveryDays', e.target.value)} className={inputClass} /></label>
          <label className="sm:col-span-2"><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Short description</span><input maxLength="280" value={form.shortDescription} onChange={(e) => setField('shortDescription', e.target.value)} className={inputClass} /></label>
          <label className="sm:col-span-2"><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Full description</span><textarea rows="5" value={form.description} onChange={(e) => setField('description', e.target.value)} className={inputClass} /></label>
          <label className="sm:col-span-2"><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Tags (comma separated)</span><input value={form.tags} onChange={(e) => setField('tags', e.target.value)} className={inputClass} /></label>
        </div>

        <section>
          <div className="mb-3 flex items-center justify-between">
            <div><h4 className="font-bold dark:text-white">Product images</h4><p className="text-xs text-gray-500">First image is the catalog cover. Up to 10 images.</p></div>
            <label className="cursor-pointer rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700">
              {uploading ? 'Uploading…' : 'Upload image'}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" disabled={uploading || form.images.length >= 10} onChange={(e) => uploadProductImage(e.target.files?.[0])} className="hidden" />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
            {form.images.map((image, index) => (
              <div key={`${image.url}-${index}`} className="relative aspect-square overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-700">
                <img src={image.url} alt="" className="h-full w-full object-cover" />
                {index === 0 && <span className="absolute left-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-bold text-white">COVER</span>}
                <button type="button" onClick={() => removeImage(index)} className="absolute right-1 top-1 h-6 w-6 rounded-full bg-red-600 text-xs text-white">✕</button>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><h4 className="font-bold dark:text-white">Inventory & variants</h4><p className="text-xs text-gray-500">Use variants for sizes, colors, or editions.</p></div>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 text-sm dark:text-gray-300"><input type="checkbox" checked={form.trackInventory} onChange={(e) => setField('trackInventory', e.target.checked)} /> Track stock</label>
              <button type="button" onClick={() => setField('variants', [...form.variants, { name: '', sku: '', stock: 0, active: true }])} className="rounded-lg border px-3 py-1.5 text-sm font-bold dark:border-gray-600 dark:text-white">+ Variant</button>
            </div>
          </div>
          {form.variants.length === 0 ? (
            <label className="mt-4 block max-w-xs"><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Product stock</span><input type="number" min="0" value={form.stock} onChange={(e) => setField('stock', e.target.value)} className={inputClass} /></label>
          ) : (
            <div className="mt-4 space-y-2">
              {form.variants.map((variant, index) => (
                <div key={variant._id || index} className="grid gap-2 rounded-lg bg-gray-50 dark:bg-gray-900 p-3 sm:grid-cols-[1fr_1fr_7rem_auto]">
                  <input required placeholder="Name (e.g. Large / Red)" value={variant.name} onChange={(e) => setField('variants', form.variants.map((v, i) => i === index ? { ...v, name: e.target.value } : v))} className={inputClass} />
                  <input placeholder="SKU" value={variant.sku} onChange={(e) => setField('variants', form.variants.map((v, i) => i === index ? { ...v, sku: e.target.value } : v))} className={inputClass} />
                  <input type="number" min="0" placeholder="Stock" value={variant.stock} onChange={(e) => setField('variants', form.variants.map((v, i) => i === index ? { ...v, stock: e.target.value } : v))} className={inputClass} />
                  <button type="button" onClick={() => setField('variants', form.variants.filter((_, i) => i !== index))} className="px-2 text-sm font-bold text-red-600">Remove</button>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm font-semibold dark:text-gray-300"><input type="checkbox" checked={form.featured} onChange={(e) => setField('featured', e.target.checked)} /> Feature in store</label>
          <div className="flex-1" />
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 dark:border-gray-600 px-5 py-2.5 font-bold dark:text-white">Cancel</button>
          <button disabled={saving || uploading} className="rounded-lg bg-blue-600 px-5 py-2.5 font-bold text-white disabled:opacity-50">{saving ? 'Saving…' : 'Save product'}</button>
        </div>
      </form>
    </AdminModal>
  );
}

function OrderManager({ order, initialStatus, onClose, onSaved }) {
  const { showNotification } = useNotification();
  const [status, setStatus] = useState(initialStatus || order.status);
  const [tracking, setTracking] = useState({
    carrier: order.tracking?.carrier || '',
    trackingNumber: order.tracking?.trackingNumber || '',
    trackingUrl: order.tracking?.trackingUrl || '',
  });
  const [adminNote, setAdminNote] = useState(order.adminNote || '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const cannotCancel = cancellationLocked(order);

  const update = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/store/orders/${order._id}/status`, { status, tracking, adminNote, reason });
      showNotification(`Order marked ${status}`, 'success');
      onSaved();
    } catch (error) {
      showNotification(error?.response?.data?.message || 'Could not update order', 'error');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm dark:text-white';
  return (
    <AdminModal title={`Manage ${order.orderNumber}`} onClose={onClose} wide>
      <div className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-5">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between border-b p-4 dark:border-gray-700"><h4 className="font-bold dark:text-white">Items</h4><GoldenAmount amount={order.totalGolden} /></div>
            {order.items.map((item, index) => <div key={index} className="flex gap-3 border-b p-4 last:border-0 dark:border-gray-700"><div className="h-14 w-14 overflow-hidden rounded-lg bg-gray-100">{item.image && <img src={item.image} alt="" className="h-full w-full object-cover" />}</div><div className="flex-1"><p className="font-semibold dark:text-white">{item.productName}</p><p className="text-xs text-gray-500">{item.variantName || item.sku} · Qty {item.quantity}</p></div><GoldenAmount amount={item.lineTotalGolden} size="sm" /></div>)}
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h4 className="font-bold dark:text-white">Customer & delivery</h4>
            <div className="mt-3 grid gap-4 text-sm sm:grid-cols-2">
              <div><p className="font-semibold dark:text-white">{order.shippingAddress.fullName}</p><p className="text-gray-500">{order.shippingAddress.email}<br />{order.shippingAddress.phone}</p></div>
              <address className="not-italic text-gray-600 dark:text-gray-300">{order.shippingAddress.addressLine1}<br />{order.shippingAddress.addressLine2 && <>{order.shippingAddress.addressLine2}<br /></>}{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}<br />{order.shippingAddress.country}</address>
            </div>
            {order.shippingAddress.deliveryNotes && <p className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 p-3 text-sm text-amber-800 dark:text-amber-300">Delivery note: {order.shippingAddress.deliveryNotes}</p>}
          </div>
        </div>
        <aside className="space-y-4">
          <div><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Current status</span><OrderStatusBadge status={order.status} /></div>
          {order.status !== 'cancelled' ? (
            <>
              <label>
                <span className="mb-1 block text-sm font-semibold dark:text-gray-200">Set status</span>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
                  {ORDER_STATUS_OPTIONS.map((item) => (
                    <option key={item} value={item} disabled={item === 'cancelled' && cannotCancel}>
                      {item}{item === order.status ? ' (current)' : ''}{item === 'cancelled' && cannotCancel ? ' — unavailable after shipping' : ''}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs text-gray-500">
                  You can correct an order to any fulfillment stage. Every change is kept in its timeline.
                </span>
              </label>
              {status === 'shipped' && <div className="space-y-3"><input required placeholder="Carrier" value={tracking.carrier} onChange={(e) => setTracking({ ...tracking, carrier: e.target.value })} className={inputClass} /><input required placeholder="Tracking number *" value={tracking.trackingNumber} onChange={(e) => setTracking({ ...tracking, trackingNumber: e.target.value })} className={inputClass} /><input placeholder="Tracking URL" value={tracking.trackingUrl} onChange={(e) => setTracking({ ...tracking, trackingUrl: e.target.value })} className={inputClass} /></div>}
              {status === 'cancelled' && <textarea required placeholder="Cancellation reason" value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} rows="3" />}
              <label><span className="mb-1 block text-sm font-semibold dark:text-gray-200">Private admin note</span><textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} className={inputClass} rows="4" /></label>
              <button onClick={update} disabled={saving || (status === 'shipped' && !tracking.trackingNumber) || (status === 'cancelled' && (!reason || cannotCancel))} className="w-full rounded-lg bg-blue-600 px-4 py-3 font-bold text-white disabled:opacity-50">{saving ? 'Updating…' : status === order.status ? 'Save order details' : `Set status to ${status}`}</button>
              {status === 'cancelled' && <p className="text-xs text-red-600">Cancelling refunds {order.totalGolden} golden tickets and restores inventory.</p>}
            </>
          ) : <p className="rounded-lg bg-gray-100 dark:bg-gray-700 p-3 text-sm text-gray-600 dark:text-gray-300">This order was cancelled and refunded, so it cannot be reopened. Create a new order if the customer wants to purchase again.</p>}
        </aside>
      </div>
    </AdminModal>
  );
}

function BulkOrderStatusModal({ orderIds, onClose, onSaved }) {
  const { showNotification } = useNotification();
  const [status, setStatus] = useState('processing');
  const [reason, setReason] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);

  const update = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/admin/store/orders/bulk-status', {
        orderIds,
        status,
        reason,
        adminNote,
      });
      if (data.failed?.length) {
        const firstFailure = data.failed[0]?.message;
        showNotification(`${data.message}${firstFailure ? `. ${firstFailure}` : ''}`, 'warning');
      } else {
        showNotification(data.message || `${orderIds.length} orders updated`, 'success');
      }
      onSaved();
    } catch (error) {
      showNotification(error?.response?.data?.message || 'Could not update selected orders', 'error');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm dark:text-white';
  return (
    <AdminModal title={`Update ${orderIds.length} selected order${orderIds.length === 1 ? '' : 's'}`} onClose={onClose}>
      <div className="space-y-5 p-5 sm:p-6">
        <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 p-4 text-sm text-blue-800 dark:text-blue-200">
          This status will override the current fulfillment stage on every eligible selected order. Each order keeps an audit entry and the customer is notified.
        </div>
        <label>
          <span className="mb-1 block text-sm font-semibold dark:text-gray-200">Set all selected orders to</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)} className={inputClass}>
            {ORDER_STATUS_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
        {status === 'shipped' && (
          <p className="rounded-lg bg-amber-50 dark:bg-amber-950/20 p-3 text-xs text-amber-800 dark:text-amber-300">
            Bulk shipping does not assign one tracking number to multiple packages. Add each order’s tracking details from Manage when available.
          </p>
        )}
        {status === 'cancelled' && (
          <>
            <textarea required placeholder="Cancellation reason for all selected orders" value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} rows="3" />
            <p className="text-xs text-red-600">Eligible orders will be refunded and their inventory restored. Orders that have shipped or were delivered will be skipped.</p>
          </>
        )}
        <label>
          <span className="mb-1 block text-sm font-semibold dark:text-gray-200">Private admin note (optional)</span>
          <textarea value={adminNote} onChange={(event) => setAdminNote(event.target.value)} className={inputClass} rows="3" placeholder="Applied to every successfully updated order" />
        </label>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 dark:border-gray-600 px-5 py-2.5 font-bold dark:text-white">Cancel</button>
          <button type="button" onClick={update} disabled={saving || (status === 'cancelled' && !reason.trim())} className="rounded-lg bg-blue-600 px-5 py-2.5 font-bold text-white disabled:opacity-50">
            {saving ? 'Updating orders…' : `Update ${orderIds.length} orders`}
          </button>
        </div>
      </div>
    </AdminModal>
  );
}

export default function StoreAdminTab() {
  const { showNotification } = useNotification();
  const [section, setSection] = useState('products');
  const [dashboard, setDashboard] = useState({});
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProduct, setShowProduct] = useState(false);
  const [managingOrder, setManagingOrder] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [showBulkStatus, setShowBulkStatus] = useState(false);
  const [updatingOrderId, setUpdatingOrderId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, list] = await Promise.all([
        api.get('/admin/store/dashboard'),
        api.get(`/admin/store/${section}`, { params: { search: search || undefined, status: status === 'all' ? undefined : status, limit: 100 } }),
      ]);
      setDashboard(dash.data || {});
      if (section === 'products') setProducts(list.data?.items || []);
      else {
        setOrders(list.data?.items || []);
        setSelectedOrderIds([]);
      }
    } catch (error) {
      showNotification(error?.response?.data?.message || 'Store admin data could not load', 'error');
    } finally {
      setLoading(false);
    }
  }, [section, search, status, showNotification]);

  useEffect(() => { load(); }, [load]);

  const archiveProduct = async (product) => {
    if (!window.confirm(`Archive ${product.name}? It will disappear from the store.`)) return;
    try {
      await api.delete(`/admin/store/products/${product._id}`);
      showNotification('Product archived', 'success');
      load();
    } catch (error) {
      showNotification(error?.response?.data?.message || 'Could not archive product', 'error');
    }
  };

  const quickUpdateOrder = async (order, nextStatus) => {
    if (nextStatus === order.status) return;
    if (['shipped', 'cancelled'].includes(nextStatus)) {
      setManagingOrder({ order, initialStatus: nextStatus });
      return;
    }
    setUpdatingOrderId(order._id);
    try {
      await api.patch(`/admin/store/orders/${order._id}/status`, { status: nextStatus });
      showNotification(`${order.orderNumber} marked ${nextStatus}`, 'success');
      await load();
    } catch (error) {
      showNotification(error?.response?.data?.message || 'Could not update order', 'error');
    } finally {
      setUpdatingOrderId('');
    }
  };

  const selectableOrderIds = orders
    .filter((order) => order.status !== 'cancelled')
    .map((order) => order._id);
  const allOrdersSelected =
    selectableOrderIds.length > 0 && selectableOrderIds.every((id) => selectedOrderIds.includes(id));

  const toggleAllOrders = () => {
    setSelectedOrderIds(allOrdersSelected ? [] : selectableOrderIds);
  };

  const toggleOrder = (orderId) => {
    setSelectedOrderIds((current) =>
      current.includes(orderId) ? current.filter((id) => id !== orderId) : [...current, orderId]
    );
  };

  const cards = [
    ['Active products', dashboard.activeProducts || 0, '🛍️'],
    ['Open orders', dashboard.openOrders || 0, '📦'],
    ['Low stock', dashboard.lowStock || 0, '⚠️'],
    ['Delivered', dashboard.deliveredOrders || 0, '✅'],
    ['Golden revenue', dashboard.goldenRevenue || 0, '🪙'],
  ];

  return (
    <div>
      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {cards.map(([label, value, icon]) => <div key={label} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"><div className="flex items-center justify-between"><p className="text-sm font-semibold text-gray-500">{label}</p><span>{icon}</span></div><p className="mt-2 text-3xl font-black text-gray-900 dark:text-white">{Number(value).toLocaleString()}</p></div>)}
      </div>
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="inline-flex rounded-xl bg-gray-100 dark:bg-gray-800 p-1">
          {['products', 'orders'].map((item) => <button key={item} onClick={() => { setSection(item); setStatus('all'); setSearch(''); setSearchDraft(''); setSelectedOrderIds([]); }} className={`rounded-lg px-5 py-2 text-sm font-bold capitalize ${section === item ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-500'}`}>{item}</button>)}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setSearch(searchDraft.trim()); }} className="flex flex-1 gap-2"><input value={searchDraft} onChange={(e) => setSearchDraft(e.target.value)} placeholder={section === 'products' ? 'Search products, SKU, category…' : 'Search order, customer, tracking…'} className="min-w-0 flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm dark:text-white" /><button className="rounded-lg border px-4 py-2 text-sm font-bold dark:border-gray-600 dark:text-white">Search</button></form>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm dark:text-white">
          <option value="all">All statuses</option>
          {(section === 'products' ? ['draft', 'active', 'archived'] : ['placed', 'processing', 'packed', 'shipped', 'delivered', 'cancelled']).map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
        {section === 'products' && <button onClick={() => { setEditingProduct(null); setShowProduct(true); }} className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white">+ Add product</button>}
      </div>

      {section === 'orders' && selectedOrderIds.length > 0 && (
        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 p-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <p className="font-bold text-blue-900 dark:text-blue-100">{selectedOrderIds.length} order{selectedOrderIds.length === 1 ? '' : 's'} selected</p>
            <p className="text-xs text-blue-700 dark:text-blue-300">Apply one fulfillment status to the full selection.</p>
          </div>
          <button onClick={() => setSelectedOrderIds([])} className="rounded-lg border border-blue-300 px-4 py-2 text-sm font-bold text-blue-700 dark:text-blue-200">Clear</button>
          <button onClick={() => setShowBulkStatus(true)} className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white">Change status</button>
        </div>
      )}

      {loading ? <div className="py-16 text-center text-gray-500">Loading store management…</div> : section === 'products' ? (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800"><tr>{['Product', 'Price', 'Inventory', 'Status', 'Sold', 'Actions'].map((head) => <th key={head} className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-500">{head}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
              {products.map((product) => {
                const stock = product.variants?.length ? product.variants.reduce((sum, variant) => sum + (variant.active ? variant.stock || 0 : 0), 0) : product.stock;
                return <tr key={product._id}><td className="px-4 py-3"><div className="flex items-center gap-3"><div className="h-12 w-12 overflow-hidden rounded-lg bg-gray-100">{product.images?.[0]?.url && <img src={product.images[0].url} alt="" className="h-full w-full object-cover" />}</div><div><p className="font-semibold text-gray-900 dark:text-white">{product.name}</p><p className="text-xs text-gray-500">{product.category}{product.sku ? ` · ${product.sku}` : ''}</p></div></div></td><td className="px-4 py-3"><GoldenAmount amount={product.priceGolden} size="sm" /></td><td className={`px-4 py-3 text-sm font-semibold ${product.trackInventory && stock <= 5 ? 'text-red-600' : 'text-gray-600 dark:text-gray-300'}`}>{product.trackInventory ? stock : 'Unlimited'}</td><td className="px-4 py-3"><span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-1 text-xs font-bold capitalize dark:text-gray-200">{product.status}</span></td><td className="px-4 py-3 text-sm dark:text-gray-300">{product.soldCount || 0}</td><td className="px-4 py-3"><div className="flex gap-2"><button onClick={() => { setEditingProduct(product); setShowProduct(true); }} className="text-sm font-bold text-blue-600">Edit</button>{product.status !== 'archived' && <button onClick={() => archiveProduct(product)} className="text-sm font-bold text-red-600">Archive</button>}</div></td></tr>;
              })}
            </tbody>
          </table>
          {!products.length && <p className="p-10 text-center text-gray-500">No products found.</p>}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800"><tr><th className="w-12 px-4 py-3"><input type="checkbox" checked={allOrdersSelected} onChange={toggleAllOrders} disabled={!selectableOrderIds.length} aria-label="Select all visible orders" className="h-4 w-4 rounded border-gray-300 text-blue-600" /></th>{['Order', 'Customer', 'Items', 'Total', 'Status', 'Date', 'Action'].map((head) => <th key={head} className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-500">{head}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
              {orders.map((order) => {
                const selected = selectedOrderIds.includes(order._id);
                const cancelled = order.status === 'cancelled';
                const cannotCancel = cancellationLocked(order);
                return (
                  <tr key={order._id} className={selected ? 'bg-blue-50/70 dark:bg-blue-950/20' : ''}>
                    <td className="px-4 py-3"><input type="checkbox" checked={selected} onChange={() => toggleOrder(order._id)} disabled={cancelled} aria-label={`Select order ${order.orderNumber}`} title={cancelled ? 'Cancelled orders cannot be reopened' : 'Select order'} className="h-4 w-4 rounded border-gray-300 text-blue-600 disabled:opacity-40" /></td>
                    <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">{order.orderNumber}</td>
                    <td className="px-4 py-3"><p className="text-sm font-semibold dark:text-white">{order.shippingAddress.fullName}</p><p className="text-xs text-gray-500">{order.shippingAddress.email}</p></td>
                    <td className="px-4 py-3 text-sm dark:text-gray-300">{order.items.reduce((sum, item) => sum + item.quantity, 0)}</td>
                    <td className="px-4 py-3"><GoldenAmount amount={order.totalGolden} size="sm" /></td>
                    <td className="px-4 py-3">
                      <select
                        value={order.status}
                        onChange={(event) => quickUpdateOrder(order, event.target.value)}
                        disabled={cancelled || updatingOrderId === order._id}
                        aria-label={`Status for ${order.orderNumber}`}
                        className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-xs font-bold capitalize dark:text-white disabled:opacity-50"
                      >
                        {ORDER_STATUS_OPTIONS.map((item) => <option key={item} value={item} disabled={item === 'cancelled' && cannotCancel}>{item}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3"><button onClick={() => setManagingOrder({ order })} className="text-sm font-bold text-blue-600">Manage</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!orders.length && <p className="p-10 text-center text-gray-500">No orders found.</p>}
        </div>
      )}

      {showProduct && <ProductEditor product={editingProduct} onClose={() => setShowProduct(false)} onSaved={() => { setShowProduct(false); load(); }} />}
      {managingOrder && <OrderManager order={managingOrder.order} initialStatus={managingOrder.initialStatus} onClose={() => setManagingOrder(null)} onSaved={() => { setManagingOrder(null); load(); }} />}
      {showBulkStatus && <BulkOrderStatusModal orderIds={selectedOrderIds} onClose={() => setShowBulkStatus(false)} onSaved={() => { setShowBulkStatus(false); load(); }} />}
    </div>
  );
}
